<template>
  <v-dialog
    :value="dialog"
    persistent
    :retain-focus="false"
    fullscreen
    @keydown.esc="escape(closeDialog)"
  >
    <v-card>
      <v-stepper v-model="stepper" class="elevation-0">
        <v-toolbar
          color="primaryBgr"
          dark
        >
          <v-row>
            <v-col cols="6" md="4">
              <div class="d-flex align-center p-h-100">
                <p-btn-icon
                  type="close"
                  btn-class="ml-n3 mr-5"
                  tabindex="4"
                  @click="escape(closeDialog)"
                ></p-btn-icon>
                <v-toolbar-title>{{ $t('contract.createContract') }}</v-toolbar-title>
              </div>
            </v-col>
            <v-col cols="6" md="4">
              <v-stepper-header class="elevation-0 p-dialog-steps-header theme--light">
                <v-stepper-step step="1">{{ $t('contract.edit.step1') }}</v-stepper-step>
                <v-divider></v-divider>
                <v-stepper-step step="2">{{ $t('contract.edit.step2') }}</v-stepper-step>
              </v-stepper-header>
            </v-col>
          </v-row>
        </v-toolbar>

        <v-stepper-items>
          <v-stepper-content step="1">
            <v-form ref="form" @submit.stop.prevent>
              <v-container>
                <v-row justify="center">
                  <v-col cols="12" sm="6" md="4" lg="3" class="text-center">
                    <v-autocomplete
                      v-model="contract.contractTemplateId"
                      :items="contractTemplates"
                      :rules="rules.contractTemplateId"
                      :label="$t('contract.edit.template')"
                      :return-object="false"
                      item-value="id"
                      item-text="name"
                      prepend-icon="mdi-file-document-outline"
                      autofocus
                      tabindex="1"
                      clearable
                      @change="onTemplateSelected"
                    ></v-autocomplete>

                    <v-text-field
                      type="text"
                      v-model.trim="contract.title"
                      :label="$t('contract.edit.title')"
                      :rules="rules.title"
                      prepend-icon="mdi-card-text-outline"
                      tabindex="1"
                      clearable
                      @input="onTitleInput"
                    ></v-text-field>
                      <v-autocomplete
                      v-for="input in templateContactInputs"
                      :key="input.name"
                      v-model="input.id"
                      :items="contactsList"
                      :rules="rules.inputValue"
                      :label="input.name"
                      item-value="id"
                      item-text="displayNameWithCompany"
                      prepend-icon="mdi-account-multiple-outline"
                    >
                      <template v-slot:item="{ item }">
                        <span :class="{ 'font-weight-bold': item.isMember }">{{ item.displayNameWithCompany }}</span>
                      </template>
                    </v-autocomplete>

                    <v-autocomplete
                      v-if="promoter"
                      v-model="templateEvent"
                      :items="eventsList"
                      :rules="rules.templateEvent"
                      :label="$t('contract.edit.templateEvent')"
                      item-value="id"
                      item-text="title"
                      prepend-icon="mdi-calendar-outline"
                      return-object
                    ></v-autocomplete>

                    <contract-signers-select
                      v-model="contract.signers"
                      :items="contactsList"
                      :label="$t('contract.edit.signers')"
                      prepend-icon="mdi-account-circle-outline"
                      tabindex="1"
                    ></contract-signers-select>

                    <v-select
                      v-model="contract.providerId"
                      :items="signingProviders"
                      :label="$t('contract.edit.provider')"
                      :return-object="false"
                      item-value="id"
                      item-text="name"
                      prepend-icon="mdi-clipboard-account-outline"
                      tabindex="1"
                      clearable
                    ></v-select>

                    <v-btn
                      class="p-btn-rounded mt-8"
                      color="primary"
                      tabindex="1"
                      large
                      rounded
                      dark
                      @click="gotoStep2"
                    >
                      {{ $t('saveAndEdit') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-container>
            </v-form>
          </v-stepper-content>

          <v-stepper-content step="2" class="px-0">
            <v-container>
              <v-row>
                <v-col>
                  <p-loading-sheet :overlay="editorOverlay">
                    <ejs-documenteditorcontainer
                      v-if="ejsReady"
                      ref="editor"
                      :enableToolbar="true"
                      :enableSearch='true'
                      :serviceUrl="editorServiceUrl"
                      :documentName="contract.title"
                      :toolbarItems="editorToolbarItems"
                      height="700px"
                      @toolbarClick="editorToolbarClick"
                      @created="onEditorCreated"
                      @contentChange="onEditorContentChange"
                    ></ejs-documenteditorcontainer>
                  </p-loading-sheet>
                  <div class="d-flex mt-8 justify-center">
                    <v-btn
                      color="primary"
                      class="p-btn-rounded mx-4"
                      tabindex="2"
                      large
                      rounded
                      dark
                      @click="submit"
                    >
                      <template v-if="finalButtonIsSave">
                        {{ $t('save') }}
                      </template>
                      <template v-else>
                        {{ $t('finish') }}
                      </template>
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-container>
          </v-stepper-content>

        </v-stepper-items>
      </v-stepper>
    </v-card>
  </v-dialog>
</template>

<script>
import Vue from 'vue'
import moment from 'moment'

import { mapActions, mapGetters } from 'vuex'

import { DocumentEditorContainerPlugin, Toolbar, Search } from '@syncfusion/ej2-vue-documenteditor'

import placeholderHelper from '@/helpers/contractPlaceholder.helper'

import PBtnIcon from '@/components/PBtnIcon'
import PLoadingSheet from '@/components/PLoadingSheet'

import ContractSignersSelect from './ContractSignersSelect'

import documentEditorMixin from '@/mixins/documentEditor.mixin'
import snackbarMixin from '@/mixins/snackbar.mixin'
import objectMixin from '@/mixins/object.mixin'
import dialogMixin from '@/mixins/dialog.mixin'
import validationMixin from '@/mixins/validation.mixin'
import unsavedChangesMixin from '@/mixins/unsavedChanges.mixin'


Vue.use(DocumentEditorContainerPlugin)


export default {
  provide: {
    DocumentEditorContainer: [Toolbar, Search]
  },


  components: {
    PBtnIcon,
    PLoadingSheet,
    ContractSignersSelect
  },


  mixins: [
    documentEditorMixin,
    snackbarMixin,
    objectMixin,
    dialogMixin,
    validationMixin,
    unsavedChangesMixin
  ],


  data () {
    return {
      editorOverlay: false,
      editorServiceUrl: process.env.VUE_APP_DOCUMENT_EDITOR_API_URL,
      stepper: 1,
      templateContactInputs: [],
      templateEvent: null,
      contract: {},
      contacts: [],
      documentEditor: null,
      ejsReady: false,
      finalButtonIsSave: false
    }
  },


  computed: {
    ...mapGetters('contractTemplate', ['contractTemplateById', 'contractTemplates']),
    ...mapGetters('signingProvider', ['signingProviders']),
    ...mapGetters('contact', ['contactById', 'shortContactById', 'shortContactsForProject']),
    ...mapGetters('event', ['eventDetailsById', 'eventsByProject']),
    ...mapGetters('member', ['membersByProject', 'memberById']),
    ...mapGetters('project', ['shortProjectById', 'currentProjectId']),
    ...mapGetters('user', ['userShortById']),

    editorToolbarItems () {
      const docxItem = this.getToolbarItem(this.toolbarItemId.docx)
      const pdfItem = this.getToolbarItem(this.toolbarItemId.pdf)

      return ['New', 'Open', 'Separator', docxItem, pdfItem, 'Separator', 'Undo', 'Redo', 'Separator', 'Image', 'Table', 'Hyperlink', 'TableOfContents', 'Separator', 'Header', 'Footer', 'PageSetup', 'PageNumber', 'Break', 'Separator', 'Find']
    },

    contactsList () {
      return this.shortContactsForProject(this.currentProjectId)
    },

    eventsList () {
      let eventsList = this.eventsByProject(this.currentProjectId).filter(event => event.isPromoter)

      eventsList = eventsList.map(event => {
        let venueTitle = event.title
        if (event.start) {
          venueTitle += ', ' + moment.utc(event.start).format('YYYY-MM-DD')
        }
        if (event.googlePlace) {
          venueTitle += ', ' + event.googlePlace
        }

        return {
          projectId: event.projectId,
          id: event.id,
          title: venueTitle
        }
      })

      return eventsList
    },

    selectedTemplate () {
      if (!this.contract.contractTemplateId) return null
      return this.contractTemplateById(this.contract.contractTemplateId)
    },

    promoter () {
      if (!this.contract.contractTemplateId) return false
      return this.selectedTemplate.promoter
    },

    rulesToApply () {
      const rule = this.rule
      const templateEvent = this.promoter ? [rule.required()] : []

      return {
        contractTemplateId: [rule.required()],
        title: [rule.required()],
        inputValue: [rule.required()],
        templateEvent
      }
    }
  },


  methods: {
    ...mapActions('contract', ['createContract', 'putContract']),
    ...mapActions('contractTemplate', ['loadContractTemplateById']),
    ...mapActions('contact', ['loadContactById']),
    ...mapActions('event', ['loadEventDetailsById']),
    ...mapActions('project', ['loadProjectById']),

    updateFinalButton () {
      this.finalButtonIsSave = this.isModelChanged()
    },

    onDialogOpen () {
      this.stepper = 1
      this.initData()
      this.initWatchedModel('fullContract')
      this.resetValidation()
    },

    updateContract () {
      this.fullContract = this.getObjectCopy(this.contract)
      this.fullContract.body = this.$refs.editor?.ej2Instances?.documentEditor?.serialize()
      this.fullContract.projectId = this.currentProjectId
      this.fullContract.contacts = [...this.templateContactInputs]
      if (this.stepper === 2) {
        this.updateFinalButton()
      }
    },

    onEditorContentChange () {
      this.updateContract()
    },

    async prepareEditor () {
      this.ejsReady = false
      await Vue.nextTick()
      this.ejsReady = true
      await Vue.nextTick()
      this.documentEditor = this.$refs.editor?.ej2Instances?.documentEditor
    },

    removeEditor () {
      this.ejsReady = false
    },

    onEditorCreated () {
      if (!this.$refs?.editor) return
      this.updateContract()
    },

    initData () {
      this.templateContactInputs = []
      this.templateEvent = null
      this.contract = {
        contractTemplateId: null,
        title: null,
        providerId: null,
        signers: [],
        status: 0,
        sent: null,
        returned: null,
        body: null,
        pdfFile: null
      }

      if (this.signingProviders?.length) {
        this.contract.providerId = this.signingProviders[0].id
      }
    },

    onTitleInput () {
      this.updateContract()
    },

    async onTemplateSelected () {
      this.updateContract()
      this.templateContactInputs = []

      try {
        this.showSnackbarProcessing()
        let template = this.contractTemplateById(this.contract.contractTemplateId)
        if (!template || !template.template) {
          template = await this.loadContractTemplateById(this.contract.contractTemplateId)
        }

        if (template) {
          this.contract.title = template.name
        }

        const templateText = template === null ? '' : template.template
        this.placeholders = placeholderHelper.parsePlaceholdersFromSfdt(templateText)

        if (template.promoter === false) {
          for (const placeholder of this.placeholders) {
            if (placeholder.name && !this.templateContactInputs.find(x => x.name === placeholder.name)) {
              this.templateContactInputs.push({ name: placeholder.name, value: null })
            }
          }
        }

        this.hideSnackbar()
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      } finally {
        this.removeEditor()
      }
    },

    async editorToolbarClick (args) {
      try {
        switch (args.item.id) {
          case this.toolbarItemId.pdf: {
            await this.saveAsPdf(this.$refs.editor, this.contract.title || 'contract')
            break
          }
          case this.toolbarItemId.docx: {
            await this.saveAsDocx(this.$refs.editor, this.contract.title || 'contract')
            break
          }
        }
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    },

    gotoStep1 () {
      this.stepper = 1
    },

    async generateContractBody () {
      this.showSnackbarProcessing()
      this.updateContract()
      delete this.fullContract.body
      if (this.fullContract?.signers?.length) {
        this.fullContract.signers = [...this.fullContract.signers.map(el => {
          return {
            id: el.id
          }
        })]
      }
      this.fullContract.eventId = this.templateEvent?.id || null
      const res = await this.createContract(this.fullContract)
      this.fullContract.id = this.contract.id = res.id
      this.fullContract.body = res.body
      this.showSnackbarSuccess(this.$t('contract.contractCreated'))
    },

    async gotoStep2 () {
      const valid = await this.validate(this.rulesToApply)
      if (!valid || !this.isModelChanged()) return
      this.stepper = 2
      await this.prepareEditor()

      try {
        this.showSnackbarProcessing(this.$t('contract.edit.opening'))
        this.editorOverlay = true
        await this.generateContractBody()
        this.documentEditor.open(this.fullContract.body)
        this.editorOverlay = false
        this.hideSnackbar()
        this.initWatchedModel('fullContract')
        this.updateFinalButton()
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    },

    async loadProjectData (projectId) {
      const project = await this.loadProjectById(projectId)
      project.members = this.membersByProject(projectId)

      const selectedContactIds = this.templateContactInputs.map(x => x.value)
      project.otherMembers = project.members.filter(member => !selectedContactIds.includes(member.contactId))

      if (project.managers.length > 0) {
        project.manager = this.userShortById(project.managers[0])
        if (project.manager && project.manager.contactId) {
          project.manager.contact = await this.loadContactById(project.manager.contactId)
        }
      }

      return project
    },

    prepareContractForPut () {
      this.preparedContract = {}
      this.preparedContract.contractTemplateId = this.contract.contractTemplateId
      this.preparedContract.id = this.fullContract.id
      this.preparedContract.projectId = this.fullContract.projectId
      this.preparedContract.title = this.fullContract.title
      this.preparedContract.body = this.fullContract.body
      this.preparedContract.signers = this.fullContract.signers
      this.preparedContract.providerId = this.fullContract.providerId
    },

    async submit () {
      const valid = await this.validate(this.rulesToApply)
      if (!valid) return

      if (this.isModelChanged()) {
        try {
          this.showSnackbarProcessing()
          this.updateContract()
          this.prepareContractForPut()
          await this.putContract(this.preparedContract)
          this.showSnackbarSuccess(this.$t('contract.contractCreated'))
          this.closeDialog()
        } catch (error) {
          this.showSnackbarError()
          this.$log.error(error)
        }
      } else {
        this.closeDialog()
      }
    }
  }
}
</script>
